import { environment } from '../environments/environment';
import { permissionGuard } from './guards/permission.guard';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { AppPermission } from './models/auth.model';
import type { Routes } from '@angular/router';

export const AppRoutes: Routes = [
  {
    path: '',
    redirectTo: environment.home,
    pathMatch: 'full',
  },
  {
    path: 'pages',
    component: AuthLayoutComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/pages/pages.module').then((x) => x.PagesModule),
      },
    ],
  },
  {
    path: '',
    component: AdminLayoutComponent,
    children: [
      {
        path: 'account',
        loadChildren: () => import('./accounts/accounts.module').then((x) => x.AccountsModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.CUSTOMERS_VIEW], title: 'Il Mio Account' },
      },
      {
        path: 'agenti',
        loadChildren: () => import('./agents/agents.routing').then((m) => m.AgentsRoutes),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.AGENTS_VIEW, AppPermission.CUSTOMERS_VIEW], title: 'Agenti e Clienti', listStateScope: 'agents' },
      },
      {
        path: 'puntiapp',
        loadChildren: () => import('./points-app/points-app.module').then((x) => x.PointsAppModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.POINTS_APP_VIEW], title: 'Punti APP' },
      },
      {
        path: 'promozioni',
        loadChildren: () => import('./promotions/promotions.module').then((x) => x.PromotionsModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.PROMOTIONS_VIEW], title: 'Promozioni', listStateScope: 'promotions' },
      },
      {
        path: 'forum',
        loadChildren: () => import('./forum/forum.module').then((x) => x.ForumModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.FORUM_VIEW, AppPermission.FORUM_MANAGE], title: 'Esperto Risponde', listStateScope: 'forum' },
      },
      {
        path: 'prodotti-evidenza',
        loadChildren: () =>
          import('./featured-products/featured-products.module').then((x) => x.FeaturedProductsModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.FEATURED_PRODUCTS_MANAGE], title: 'Prodotti in Evidenza' },
      },
      {
        path: 'categorie',
        loadChildren: () => import('./categories/categories.module').then((x) => x.CategoriesModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.CATEGORIES_VIEW], mode: 'sconti', title: 'Sconti per Categoria' },
      },
      {
        path: 'prodotti-correlati',
        loadChildren: () => import('./related-products/related-products.module').then((x) => x.RelatedProductsModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.RELATED_PRODUCTS_MANAGE], title: 'Prodotti Correlati' },
      },
      {
        path: 'annunci-mercatino',
        loadChildren: () =>
          import('./marketplace-announcements/marketplace-announcements.module').then((x) => x.MarketplaceModule),
        canActivate: [permissionGuard],
        data: {
          permissions: [AppPermission.MARKETPLACE_ANNOUNCEMENTS_VIEW, AppPermission.MARKETPLACE_ANNOUNCEMENTS_MANAGE],
          logicalOp: 'OR',
          title: 'Annunci Mercatino',
          listStateScope: 'marketplace',
          showBackBtn: false,
        },
      },
      {
        path: 'categorie-app',
        canActivate: [permissionGuard],
        data: {
          permissions: [AppPermission.MARKETPLACE_CATEGORIES_VIEW],
          title: 'Categorie',
          showBackBtn: false,
        },
        loadChildren: () => import('./App-categories/app-categories.module').then((m) => m.AppCategoriesModule),
      },
      {
        path: 'notifiche-push',
        loadChildren: () =>
          import('./push-notifications/push-notifications.module').then((x) => x.PushNotificationsModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.NOTIFICATIONS_PUSH_MANAGE], title: 'Notifiche Push' },
      },
      {
        path: 'components',
        loadChildren: () => import('./pages/components/components.module').then((x) => x.ComponentsPageModule),
        data: { title: 'Componenti' },
      },
      {
        path: 'prodotti',
        loadChildren: () => import('./products/products.module').then((x) => x.ProductsModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.PRODUCTS_VIEW], mode: 'prodotti', title: 'Prodotti', listStateScope: 'products' },
      },
      {
        path: 'catalogo-prodotti',
        loadChildren: () => import('./products-catalog/products-catalog.module').then((x) => x.ProductsCatalogModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.PRODUCTS_CATALOG_VIEW], title: 'Catalogo Prodotti', listStateScope: 'products-catalog' },
      },
      {
        path: 'ordini',
        loadChildren: () => import('./orders/orders.module').then((x) => x.OrdersModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.ORDERS_VIEW], title: 'Ordini', listStateScope: 'orders' },
      },
      {
        path: 'report',
        loadChildren: () => import('./reports/reports.module').then((x) => x.ReportsModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.REPORT_VIEW], title: 'Statistiche vendita', listStateScope: 'reports' },
      },
      {
        path: 'accounting',
        loadChildren: () => import('./accounting/accounting.module').then((x) => x.AccountingModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.ACCOUNTING_VIEW], title: 'Estratto conto', listStateScope: 'account-statement' },
      },
      {
        path: 'app-versions',
        loadChildren: () => import('./app-versions/app-versions.module').then((x) => x.AppVersionsModule),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.APP_VERSIONS_VIEW], title: 'Versioni App', listStateScope: 'app-versions' },
      },
      {
        path: 'user-management',
        loadChildren: () => import('./user-management/user-management.routes').then((m) => m.USER_MANAGEMENT_ROUTES),
        canActivate: [permissionGuard],
        data: { permissions: [AppPermission.USERS_MANAGE], listStateScope: 'users' },
      },
      {
        path: 'gestione-categorie-manutenzione',
        loadChildren: () =>
          import('./maintenance-categories/maintenance-categories.module').then(
            (m) => m.MaintenanceCategoriesModule,
          ),
        canActivate: [permissionGuard],
        data: {
          permissions: [AppPermission.MAINTENANCE_CATEGORIES_MANAGE],
          title: 'Gestione Categorie',
          showBackBtn: false,
        },
      },
      {
        path: 'app',
        canActivate: [permissionGuard],
        children: [
          {
            path: 'punti',
            children: [
              {
                path: 'categorie',
                loadChildren: () => import('./categories/categories.module').then((m) => m.CategoriesModule),
                data: { permissions: [AppPermission.POINTS_CATEGORIES_VIEW], mode: 'punti' },
              },
              {
                path: 'prodotto',
                loadChildren: () => import('./products/products.module').then((m) => m.ProductsModule),
                canActivate: [permissionGuard],
                data: { permissions: [AppPermission.POINTS_MANAGE], mode: 'punti', listStateScope: 'product-points' },
              },
            ],
          },
          {
            path: 'catalogo-premi',
            canActivate: [permissionGuard],
            data: { permissions: [AppPermission.GIFT_CATALOGUE_VIEW] },
            loadChildren: () => import('./gift-catalogue/gift-catalogue.module').then((x) => x.GiftCatalogueModule),
          },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/agenti/lista',
  },
];
